'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { getProductConstraint, isCouponProduct } from '../../../../../schema/product/entry.js';
import { getEventNumber } from '../../../../../schema/sport/entry.js';
import { useEngine } from '../../engine/hooks/useEngine.js';
import { useSearchParameters } from '../../lib/hooks/useSearchParameters.js';
import { useOpenedDraws } from '../hooks/useOpenedDraws.js';
import { DrawContext } from './DrawsContext.js';
/**
 * Draw provider that provides information about the current selected draw among the opened draws, based on the user interaction.
 * Pulls information from the URL query string and the current state to determine which draw is selected.
 *
 * @param param Props
 * @param param.children Component Children
 * @param param.drawNumber Optional draw number
 * @param param.product Optional product definition
 * @returns Provider for {@link DrawContext}
 */
export function OpenedDrawsProvider({
  children,
  drawNumber: draw,
  product
}) {
  const params = useSearchParameters('event');
  const definition = useEngine();
  const {
    data: openedDraws
  } = useOpenedDraws(definition.moduleEngine);
  const [firstOpenedProductDraw] = openedDraws;
  const currentProductDrawNumber = firstOpenedProductDraw?.draw.drawNumber;
  const currentProductDrawProduct = firstOpenedProductDraw?.draw.product;
  const productDrawEventNumber = getEventNumber(1);
  const drawInfo = useMemo(() => {
    let drawNumber;
    let productType;
    let eventNumber = getEventNumber(1);
    // 1. User has entered a correct draw in the query string
    if (draw) {
      drawNumber = draw;
    }
    // 2. Set first opened draw if empty query string
    else if (currentProductDrawNumber) {
      drawNumber = currentProductDrawNumber;
    }
    // 1. User has entered a correct product in the query string
    if (product && definition.isProductIncluded(product)) {
      productType = product;
    }
    // 2. Set first opened product if empty query string
    else if (currentProductDrawProduct && isCouponProduct(currentProductDrawProduct)) {
      productType = currentProductDrawProduct;
    }
    // 3. Set first product from engine if no draws are opened
    else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const engineProduct = definition.drawProducts[0];
      productType = engineProduct;
    }
    // 1. User has entered a correct event in the query string
    if (params.event) {
      eventNumber = params.event;
    }
    // 2. Set event number from first opened draw if empty query string
    else if (productDrawEventNumber) {
      eventNumber = productDrawEventNumber;
    }
    return {
      product: getProductConstraint(productType),
      eventNumber,
      drawNumber
    };
  }, [params.event, currentProductDrawNumber, definition, currentProductDrawProduct, productDrawEventNumber, draw, product]);
  return _jsx(DrawContext.Provider, {
    value: drawInfo,
    children: children
  });
}